import React, { useState, ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PanelLeft, Plus, Search, X } from "lucide-react";
import { Button } from "./ui/button";
import { Tooltip } from "./ui/tooltip";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";

interface MasterDetailLayoutProps {
  // Master panel props
  masterTitle: string;
  masterItems: ReactNode;
  masterEmptyState?: ReactNode;

  // Detail panel props
  detailContent: ReactNode;
  detailEmptyState?: ReactNode;

  // Actions
  onNewItem?: () => void;
  newItemLabel?: string;
  showNewButton?: boolean;

  // Search/filter
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;

  // Optional filters/controls in header
  headerControls?: ReactNode;

  // Optional props
  className?: string;
  masterWidth?: string; // e.g., "w-80" or "w-1/4"
  selectedBackground?: string;
}

export const MasterDetailLayout: React.FC<MasterDetailLayoutProps> = ({
  masterTitle,
  masterItems,
  masterEmptyState,
  detailContent,
  detailEmptyState,
  onNewItem,
  newItemLabel = "New Item",
  showNewButton = true,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  showSearch = true,
  headerControls,
  className,
  masterWidth = "w-80",
  selectedBackground,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const layoutStyle = selectedBackground
    ? ({ "--selected-bg": selectedBackground } as React.CSSProperties)
    : undefined;

  return (
    <div className={cn("flex h-full", className)} style={layoutStyle}>
      {/* Sidebar Toggle Button (mobile/collapsed) */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className={cn(
          "fixed top-16 left-4 z-50 p-2 rounded-lg bg-card border border-border shadow-sm transition-opacity",
          isSidebarOpen ? "opacity-0 pointer-events-none" : "opacity-100"
        )}
        title="Open sidebar"
      >
        <PanelLeft className="w-5 h-5" />
      </button>

      {/* Master Panel (Sidebar) */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.aside
            initial={{ x: -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -320, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "flex flex-col border-r bg-card shrink-0",
              masterWidth
            )}
          >
            {/* Master Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-1 hover:bg-accent rounded transition-colors shrink-0"
                  title="Close sidebar"
                >
                  <PanelLeft className="w-5 h-5" />
                </button>
                <h2 className="text-lg font-semibold truncate">{masterTitle}</h2>
              </div>
            </div>

            {/* Search Bar */}
            {showSearch && onSearchChange && (
              <div className="px-4 py-3 border-b bg-muted/30">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchValue && (
                    <button
                      onClick={() => onSearchChange("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-accent rounded transition-colors"
                      title="Clear search"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Optional Header Controls */}
            {headerControls && (
              <div className="px-4 py-3 border-b bg-muted/30">
                {headerControls}
              </div>
            )}

            {/* Master List */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              {masterItems || masterEmptyState}
            </div>

            {/* New Item Button */}
            {showNewButton && onNewItem && (
              <div className="p-4 border-t">
                <Button
                  onClick={onNewItem}
                  className="w-full"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {newItemLabel}
                </Button>
              </div>
            )}
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Detail Panel (Main Content) */}
      <div className="flex-1 overflow-y-auto">
        {detailContent || detailEmptyState || (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                <PanelLeft className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Selection</h3>
              <p className="text-sm text-muted-foreground">
                Select an item from the sidebar to view details
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Reusable Master List Item Component
interface MasterListItemProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  isSelected?: boolean;
  onClick?: () => void;
  actions?: ReactNode;
  badge?: ReactNode;
}

export const MasterListItem: React.FC<MasterListItemProps> = ({
  title,
  subtitle,
  icon,
  isSelected,
  onClick,
  actions,
  badge,
}) => {
  const selectedStyle = isSelected
    ? ({ backgroundColor: "var(--selected-bg)" } as React.CSSProperties)
    : undefined;

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left pl-4 pr-8 py-3 border-b transition-colors",
        "hover:bg-accent/50",
        isSelected && "bg-accent border-l-4 border-l-primary"
      )}
      style={selectedStyle}
    >
      <div className="flex items-start gap-3">
        {icon && <div className="shrink-0 mt-0.5">{icon}</div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Tooltip content={title} side="bottom">
              <h3 className="font-medium truncate">{title}</h3>
            </Tooltip>
            {badge}
          </div>
          {subtitle && (
            <Tooltip content={subtitle} side="bottom">
              <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
            </Tooltip>
          )}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
    </button>
  );
};

// Reusable Detail Panel Header Component
interface DetailPanelHeaderProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  actions?: ReactNode;
}

export const DetailPanelHeader: React.FC<DetailPanelHeaderProps> = ({
  title,
  subtitle,
  icon,
  actions,
}) => {
  return (
    <div className="border-b bg-card sticky top-0 z-10">
      <div className="px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {icon && <div className="shrink-0 mt-1">{icon}</div>}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-semibold mb-1">{title}</h1>
              {subtitle && (
                <p className="text-sm text-muted-foreground">{subtitle}</p>
              )}
            </div>
          </div>
          {actions && <div className="shrink-0 flex gap-2">{actions}</div>}
        </div>
      </div>
    </div>
  );
};

// Reusable Empty State Component
interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
}) => {
  return (
    <div className="flex items-center justify-center h-full p-6">
      <div className="text-center max-w-md">
        {icon && (
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
            {icon}
          </div>
        )}
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        {description && (
          <p className="text-sm text-muted-foreground mb-4">{description}</p>
        )}
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
};
